import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppData } from "@/context/AppDataContext";
import {
  buildAgentTeamIndex,
  buildTeamOptions,
  groupTeamOptionsByHoS,
  resolveTeamForAgentName,
  teamIdsForHeadOfSales,
  teamIdsForTeamManager,
  type TeamOption,
} from "@/helpers/agentTeamMapping";
import type { MultiSelectGroup } from "@/components/ui/multi-select";

/** Shared across the Dashboard's Open Requests widget and the All Requests page. */
const STORAGE_KEY = "uw-recent-activity-team-filter";

interface StoredSelection {
  /** The worker whose selection this is, so switching users on a shared profile doesn't leak. */
  userGuid: string;
  teamIds: string[];
}

function readStoredSelection(userGuid: string | undefined): string[] | null {
  if (!userGuid) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSelection> | string[] | null;
    // Older format was a bare string[] with no user scoping — ignore it so it doesn't leak
    // across users.
    if (!parsed || Array.isArray(parsed)) return null;
    if (parsed.userGuid !== userGuid) return null;
    return Array.isArray(parsed.teamIds) ? parsed.teamIds.filter((v): v is string => typeof v === "string") : null;
  } catch {
    return null;
  }
}

function writeStoredSelection(userGuid: string | undefined, ids: string[]): void {
  if (!userGuid) return;
  try {
    const payload: StoredSelection = { userGuid, teamIds: ids };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage failures (private browsing, quota, etc.) — filter still works in-memory.
  }
}

interface ActivityLike {
  agentName?: string | null;
}

export interface UseTeamFilterResult {
  /** All selectable teams, deduplicated from the sales hierarchy. */
  teamOptions: TeamOption[];
  /** Teams grouped by Head of Sales, for the grouped MultiSelect dropdown. */
  groupedTeamOptions: MultiSelectGroup[];
  /** Currently selected team ids. An empty array means "no filter" (show everyone). */
  selectedTeamIds: string[];
  /** Updates the selection and persists it. */
  setSelectedTeamIds: (ids: string[]) => void;
  /** Filters a list of activities by the current team selection. */
  filterActivities: <T extends ActivityLike>(activities: T[]) => T[];
}

/**
 * Multi-select Team filter for Recent Activity / All Requests.
 *
 * Recent Activity only returns `agentName` as a string, so this hook builds a name -> team ->
 * manager index from the app-wide `workers` + `salesHierarchy` data (see agentTeamMapping.ts),
 * and defaults the selection to "all of my teams" for Heads of Sales / Team Managers.
 */
export function useTeamFilter(): UseTeamFilterResult {
  const { workers, salesHierarchy, departments, currentUser } = useAppData();

  const agentTeamIndex = useMemo(() => buildAgentTeamIndex(workers, salesHierarchy), [workers, salesHierarchy]);
  const teamOptions = useMemo(() => buildTeamOptions(salesHierarchy, departments), [salesHierarchy, departments]);
  const groupedTeamOptions = useMemo(() => groupTeamOptionsByHoS(teamOptions), [teamOptions]);

  const [selectedTeamIds, setSelectedTeamIdsState] = useState<string[]>([]);
  // Tracks whether we've resolved a starting selection (stored or default) for the current
  // user, so the effect below only runs once per user until `teamOptions` first loads.
  const resolvedForUserRef = useRef<string | null>(null);

  useEffect(() => {
    const userGuid = currentUser?.userGuid;
    if (!userGuid || teamOptions.length === 0) return;
    if (resolvedForUserRef.current === userGuid) return;

    const validTeamIds = new Set(teamOptions.map((t) => t.id));
    const stored = readStoredSelection(userGuid);
    // Drop any ids that no longer correspond to a loaded team (renamed/removed since storing).
    const storedValid = stored?.filter((id) => validTeamIds.has(id)) ?? null;

    let initial: string[];
    if (stored !== null) {
      initial = storedValid ?? [];
    } else {
      const asHos = teamIdsForHeadOfSales(teamOptions, currentUser.name);
      const asTeamManager = asHos.length === 0 ? teamIdsForTeamManager(teamOptions, currentUser.name) : [];
      initial = asHos.length > 0 ? asHos : asTeamManager;
    }

    resolvedForUserRef.current = userGuid;
    setSelectedTeamIdsState(initial);
    writeStoredSelection(userGuid, initial);
  }, [currentUser, teamOptions]);

  const setSelectedTeamIds = useCallback(
    (ids: string[]) => {
      setSelectedTeamIdsState(ids);
      writeStoredSelection(currentUser?.userGuid, ids);
    },
    [currentUser?.userGuid]
  );

  const filterActivities = useCallback(
    <T extends ActivityLike>(activities: T[]): T[] => {
      if (selectedTeamIds.length === 0) return activities;
      return activities.filter((activity) => {
        const team = resolveTeamForAgentName(agentTeamIndex, activity.agentName);
        return team !== null && selectedTeamIds.includes(team.departmentId);
      });
    },
    [selectedTeamIds, agentTeamIndex]
  );

  return {
    teamOptions,
    groupedTeamOptions,
    selectedTeamIds,
    setSelectedTeamIds,
    filterActivities,
  };
}
